import type { ViewEventModel } from 'functions/app/event/view'

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function formatCalendarDate(ts: number): string {
  const d = new Date(ts)
  return [
    d.getUTCFullYear(),
    pad(d.getUTCMonth() + 1),
    pad(d.getUTCDate()),
    'T',
    pad(d.getUTCHours()),
    pad(d.getUTCMinutes()),
    pad(d.getUTCSeconds()),
    'Z',
  ].join('')
}

export function googleCalendarUrl(event: ViewEventModel): string {
  const title = event.title
  const dates = [
    formatCalendarDate(event.start),
    formatCalendarDate(event.end),
  ].join('/')
  const details = event.intro ?? ''
  const location = event.location?.address ?? ''

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates,
  })

  if (details) params.set('details', details)
  if (location) params.set('location', location)

  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

export function outlookCalendarUrl(event: ViewEventModel): string {
  const title = event.title
  const startDt = new Date(event.start).toISOString()
  const endDt = new Date(event.end).toISOString()
  const body = event.intro ?? ''
  const location = event.location?.address ?? ''

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
  })

  params.set('subject', title)
  params.set('startdt', startDt)
  params.set('enddt', endDt)

  if (body) params.set('body', body)
  if (location) params.set('location', location)

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`
}

function escapeICS(text: string): string {
  return text.replace(/[\\;,]/g, (c) => '\\' + c).replace(/\n/g, '\\n')
}

export function buildIcsContent(event: ViewEventModel): string {
  const now = formatCalendarDate(Date.now())
  const dtStart = formatCalendarDate(event.start)
  const dtEnd = formatCalendarDate(event.end)

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//CrowdBuilding//NONSGML Agenda//NL',
    'BEGIN:VEVENT',
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `DTSTAMP:${now}`,
    `UID:${event.uuid}@crowdbuilding.io`,
    `SUMMARY:${escapeICS(event.title)}`,
  ]

  if (event.intro) {
    lines.push(`DESCRIPTION:${escapeICS(event.intro)}`)
  }

  if (event.location?.address) {
    lines.push(`LOCATION:${escapeICS(event.location.address)}`)
  }

  lines.push('END:VEVENT')
  lines.push('END:VCALENDAR')

  return lines.join('\r\n')
}

export function downloadIcs(event: ViewEventModel): void {
  if (typeof window === 'undefined') return

  const content = buildIcsContent(event)
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = `${event.slug}.ics`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
